import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { Navbar } from '../../shared/components/layout';
import Sidebar from '../../shared/components/layout/Sidebar';
import { ErrorBoundary } from '../../shared/components';
import { useAuth } from '../../app/providers';
import { useEffect, useRef, useState } from 'react';
import { onboardingApi } from '../../features/onboarding/api';

export const RootLayout = () => {
  const { isAuthenticated, user, hasOrganization, setHasOrganization } =
    useAuth();
  console.log('user', user);
  const routerState = useRouterState();
  const mainRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // State for mobile sidebar
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Check subscription status
  const validStatuses = ['ACTIVE', 'TRIALING', 'PAST_DUE'];

  // scroll to top whenever the route changes
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [routerState.location.pathname]); // triggers when URL changes

  // Check if user has organization (for OWNER/MANAGER)
  useEffect(() => {
    const checkOrganization = async () => {
      if (user?.role === 'OWNER' || user?.role === 'MANAGER') {
        try {
          const org = await onboardingApi.getOrganization();
          setHasOrganization(!!org);
        } catch (error) {
          console.error('Failed to fetch organization:', error);
          setHasOrganization(false);
        }
      } else {
        setHasOrganization(null);
      }
    };

    if (user) {
      checkOrganization();
    }
  }, [user, setHasOrganization]);

  // Navigation logic
  useEffect(() => {
    console.log('user in root layout', user);

    // PENDING role - needs to choose role
    if (user?.role === 'PENDING') {
      console.log('user did not choose any role');
      navigate({ to: '/onboarding/role' });
      return;
    }

    // RESIDENT role - incomplete onboarding
    if (
      user?.role === 'RESIDENT' &&
      !user?.isOnboardingComplete &&
      !routerState.location.pathname.startsWith('/resident/onboarding/houses/')
    ) {
      console.log('resident is not completed onboarding');
      console.log('Current path:', routerState.location.pathname);
      console.log('Redirecting to onboarding houses...');
      navigate({ to: '/resident/onboarding/houses' });
      return;
    }

    // OWNER/MANAGER role - incomplete onboarding
    if (
      (user?.role === 'OWNER' || user?.role === 'MANAGER') &&
      !user?.isOnboardingComplete
    ) {
      console.log('user role', user?.role);
      console.log('user isOnboardingComplete', user?.isOnboardingComplete);

      // New Owner? No subscription? Redirect to packages
      const hasValidSubscription = 
        user.subscription && validStatuses.includes(user.subscription.status);

      if (user.role === 'OWNER' && !hasValidSubscription) {
        if (routerState.location.pathname !== '/onboarding/packages') {
           console.log('Incomplete onboarding & no subscription -> redirecting to packages');
           navigate({ to: '/onboarding/packages' });
        }
        return;
      }

      // Already on onboarding pages? Don't redirect (prevents loops)
      if (routerState.location.pathname.startsWith('/onboarding/')) {
        console.log('already on onboarding page, staying here');
        return;
      }

      // Still loading organization status? Wait
      if (hasOrganization === null) {
        console.log('still loading organization status');
        return;
      }

      // Has organization? Go to finalize page
      if (hasOrganization) {
        console.log('has organization, redirecting to finalize');
        navigate({ to: '/onboarding/finalize' });
        return;
      }

      // No organization? Go to organization setup
      console.log('no organization, redirecting to organization setup');
      navigate({ to: '/onboarding/organization' });
      return;
    }

    // Onboarding complete - redirect to dashboard if on onboarding pages
    if (
      user?.isOnboardingComplete &&
      routerState.location.pathname.startsWith('/onboarding/')
    ) {
      console.log('user is completed onboarding, redirecting to dashboard');
      navigate({ to: '/' });
      return;
    }

    // Manager without organization - redirect to no-organization page
    if (user?.role === 'MANAGER' && hasOrganization === false) {
      console.log('manager does not have any organization');
      // Only redirect if not already on the no-organization page
      if (routerState.location.pathname !== '/no-organization') {
        navigate({ to: '/no-organization' });
      }
      return;
    }
    // Subscription Expired Check
    if (
      user?.role === 'OWNER' &&
      user?.isOnboardingComplete
    ) {
      const isValid = user?.subscription && validStatuses.includes(user.subscription.status);
      if (!isValid && routerState.location.pathname !== '/subscription/expired') {
        console.log('Subscription expired -> redirecting to renewal page');
        navigate({ to: '/subscription/expired' });
        return;
      }
    }
  }, [routerState.location.pathname, user, hasOrganization, navigate]);

  // Define public routes where sidebar should NOT be shown
  const publicRoutes = [
    '/home',
    '/features',
    '/about-us',
    '/contact-us',
    '/resident/onboarding/houses',
    '/onboarding/role',
    '/onboarding/organization',
    '/onboarding/finalize',
    '/no-organization',
    '/resident/onboarding/houses/payment-success',
    '/invite',
    '/onboarding/packages',
    '/subscription/expired',
  ];
  const currentPath = routerState.location.pathname;
  const isPublicRoute =
    publicRoutes.includes(currentPath) ||
    currentPath.startsWith('/resident/onboarding/houses/');

  // Show loading state while checking organization status for managers
  // This prevents flickering of the sidebar
  if (user?.role === 'MANAGER' && hasOrganization === null) {
    return (
      <ErrorBoundary>
        <div className="h-screen flex flex-col bg-gray-50">
          <header className="sticky top-0 z-50">
            <Navbar />
          </header>
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          </main>
        </div>
      </ErrorBoundary>
    );
  }

  // Show sidebar only if authenticated AND not on a public route
  // Also hide sidebar if manager doesn't have an organization
  const shouldShowSidebar =
    isAuthenticated &&
    !isPublicRoute &&
    !(user?.role === 'MANAGER' && hasOrganization === false);

  return (
    <ErrorBoundary>
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Sticky Navbar */}
        <header className="sticky top-0 z-50">
          <Navbar onMenuClick={() => setIsSidebarOpen(true)} />
        </header>

        {/* Main layout: sidebar + scrollable content */}
        {shouldShowSidebar ? (
          <div className="flex flex-1 overflow-hidden  relative">
            {/* Sidebar (Includes mobile overlay & button) */}
            <Sidebar
              isOpen={isSidebarOpen}
              onClose={() => setIsSidebarOpen(false)}
            />

            {/* Scrollable content */}
            <main ref={mainRef} className="flex-1 overflow-y-auto p-4 lg:p-8">
              <Outlet />
            </main>
          </div>
        ) : (
          <main ref={mainRef} className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        )}
      </div>

      {process.env.NODE_ENV === 'development' && <TanStackRouterDevtools />}
    </ErrorBoundary>
  );
};
